import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { getSocket } from "../../../services/socket";
import {
  tournamentsRegisterRequest,
  tournamentsUnregisterRequest,
} from "../../../store/tournaments/tournamentsActions";
import { selectTournaments } from "../../../store/tournaments/tournamentsReducer";
import { ProfileImage } from "../../../assets";
import type { SnG } from "../Middle/TournamentsSection";

export type UseTournamentRegistrationParams = {
  tournamentId: string | undefined;
  roomId: string | undefined;
  /** Optional. If not provided, sng is resolved from Redux by tournamentId. */
  sng?: SnG | null;
};

export type SignedPlayer = {
  id: string | number;
  name: string;
  score: number;
  chips: any;
  avatarImage: { uri: string } | number;
};

export function useTournamentRegistration({
  tournamentId,
  roomId,
  sng: sngProp,
}: UseTournamentRegistrationParams) {
  const auth = useSelector((state: RootState) => state.auth);
  const playerId = auth?.user?.id;
  const username = auth?.user?.username;
  const tournaments = useSelector(selectTournaments);
  const registerLoading = useSelector(
    (state: RootState) => state.tournaments.registerLoading,
  );
  const dispatch = useDispatch();

  const sng: SnG | undefined =
    sngProp ?? tournaments.find((t) => String(t.id) === String(tournamentId));

  const [players, setPlayers] = useState<any[]>([]);
  const [details, setDetails] = useState<any>(null);

  const signedPlayers = useMemo((): SignedPlayer[] => {
    const source = players.length
      ? players
      : Array.isArray((sng as any)?.players)
        ? (sng as any).players
        : [];

    return source.map((p: any) => ({
      id: p.id,
      name: p.username,
      score: p.score ?? 0,
      chips: p.chips,
      avatarImage: p.avatar ? { uri: p.avatar } : ProfileImage,
    }));
  }, [players, (sng as any)?.players]);

  const isSigned = useMemo(() => {
    if (!playerId) return false;
    if (sng?.registered === false) return false;
    if (signedPlayers.some((p) => String(p.id) === String(playerId))) return true;
    return sng?.registered === true;
  }, [signedPlayers, playerId, sng?.registered]);

  // Socket listener for getSnGTournamentDetails
  useEffect(() => {
    if (!playerId || !username || !tournamentId || !roomId) return;

    const socket = getSocket(String(playerId), String(username));

    const handler = (packet: any) => {
      const data = packet?.data ?? packet;
      setDetails(data);
      if (Array.isArray(data.players)) {
        setPlayers(data.players);
      }
    };

    socket.on("getSnGTournamentDetails", handler);
    return () => {
      socket.off("getSnGTournamentDetails", handler);
    };
  }, [playerId, username, tournamentId, roomId]);

  // Initial fetch and refetch when sng changes
  const refetch = React.useCallback(() => {
    if (!playerId || !username || !tournamentId || !roomId) return;
    const socket = getSocket(String(playerId), String(username));
    socket.emit("tournament:getSnGTournamentDetails", {
      tournament_id: tournamentId,
      room: roomId,
    });
  }, [playerId, username, tournamentId, roomId]);

  useEffect(() => {
    if (!playerId || !username || !tournamentId || !roomId || !sng) return;
    refetch();
  }, [playerId, username, tournamentId, roomId, sng?.id, refetch]);

  // Refetch after register/unregister completes
  const prevLoadingRef = React.useRef<boolean>(false);
  const prevRegisteredRef = React.useRef<boolean | undefined>(sng?.registered);

  useEffect(() => {
    if (!playerId || !username || !tournamentId || !roomId) return;

    const currentRegistered = sng?.registered;
    const prevRegistered = prevRegisteredRef.current;

    const loadingCompleted =
      prevLoadingRef.current === true && registerLoading === false;
    const registeredStatusChanged =
      prevRegistered !== undefined && currentRegistered !== prevRegistered;

    if (loadingCompleted || registeredStatusChanged) {
      refetch();
    }

    prevLoadingRef.current = registerLoading;
    prevRegisteredRef.current = currentRegistered;
  }, [
    playerId,
    username,
    tournamentId,
    roomId,
    registerLoading,
    sng?.registered,
    refetch,
  ]);

  const handleRegisterPress = React.useCallback(() => {
    if (!tournamentId || !playerId) return;

    if (isSigned) {
      dispatch(tournamentsUnregisterRequest({ tournamentId }));
      return;
    }
    dispatch(tournamentsRegisterRequest({ tournamentId }));
  }, [tournamentId, playerId, isSigned, dispatch]);

  return {
    playerId,
    username,
    players,
    setPlayers,
    details,
    setDetails,
    signedPlayers,
    isSigned,
    registerLoading,
    sng,
    handleRegisterPress,
    refetch,
  };
}
